import React from "react";
import { useLocation } from "react-router-dom";
import { linkReplaceFn } from "../../utils/custom_funtions";
import logo from "../../assets/img/motion_new_logo.png";

const SampleQuestionModuleRender = () => {
  const location = useLocation();
  const responseData = location.state?.responseData || [];
  const language = location.state?.language || "english";
  const module = location.state?.module || "question";
  const isSolutionModule = module === "solution";

  // Extract options from HTML string
  const extractOptions = (htmlString) => {
    if (!htmlString) return [];
    return htmlString.split("@@@").map((html) => {
      const doc = new DOMParser().parseFromString(html, "text/html");
      return doc.body.textContent.trim(); // Extract text content from HTML
    });
  };

  // Group questions by exercise level, type, sub_topic, and sub_sub_topic with section persistence
  const groupByExerciseAndTopics = () => {
    const grouped = {};
    let currentExerciseLevel = null;
    let currentQuestionType = null;
    
    responseData.forEach(question => {
      // Update exercise level and question type only if they exist in the current question
      if (question.section) {
        currentExerciseLevel = question.section;
      }
      
      if (question.section_type) {
        currentQuestionType = question.section_type;
      }
      
      // Use current values or defaults if not set
      const exerciseLevel = currentExerciseLevel;
      const questionType = currentQuestionType ;
      
      // Get sub_topic and sub_sub_topic (with defaults if not present)
      const subTopic = question.sub_topic || "General";
      const subSubTopic = question.sub_sub_topic || "General";
      
      // Ensure nested objects exist
      if (!grouped[exerciseLevel]) {
        grouped[exerciseLevel] = {};
      }
      
      if (!grouped[exerciseLevel][questionType]) {
        grouped[exerciseLevel][questionType] = {};
      }
      
      if (!grouped[exerciseLevel][questionType][subTopic]) {
        grouped[exerciseLevel][questionType][subTopic] = {};
      }
      
      if (!grouped[exerciseLevel][questionType][subTopic][subSubTopic]) {
        grouped[exerciseLevel][questionType][subTopic][subSubTopic] = [];
      }
      
      // Add question to appropriate group
      grouped[exerciseLevel][questionType][subTopic][subSubTopic].push(question);
    });
    
    return grouped;
  };

  const groupedQuestions = groupByExerciseAndTopics();

  // Function to split questions into left and right columns
  const splitQuestionsIntoColumns = (questions) => {
    const leftColumn = [];
    const rightColumn = [];
    
    questions.forEach((question, index) => {
      if (index % 2 === 0) {
        leftColumn.push(question);
      } else {
        rightColumn.push(question);
      }
    });
    
    return { leftColumn, rightColumn };
  };

  // Render solution only (for solution module)
  const renderSolution = (question, index) => {
    const solutionHtml = linkReplaceFn(
      language === "english"
        ? question.solution || question.sol_text || ""
        : question.solution_hin || question.solution_hindi || question.solution || ""
    );

    return (
      <div key={index} style={styles.questionItem} className="sample-question-item">
        <div style={styles.questionRow}>
          <div style={styles.questionNumber}>{index + 1}.</div>
          <div style={styles.solutionHeading}>Solution</div>
        </div>
        <div
          style={styles.solutionText}
          dangerouslySetInnerHTML={{ __html: solutionHtml || "—" }}
        />
      </div>
    );
  };

  // Render a single question
  const renderQuestion = (question, index, columnsTotal) => {
    return (
      <div key={index} style={styles.questionItem} className="sample-question-item">
        {/* Question Row */}
        <div style={styles.questionRow}>
          <div style={styles.questionNumber}>{index + 1}.</div>
          <div 
            style={styles.questionText} 
            dangerouslySetInnerHTML={{ 
              __html: linkReplaceFn(language === "english" ? question.cq_text : question.q_text_hindi) 
            }} 
          />
          
          {/* QR Code if available */}
          {question.qr_code && (
            <div style={styles.qrCode}>
              <img src={question.qr_code} alt="QR Code" />
            </div>
          )}
        </div>
        
        {/* Options Grid */}
        <div style={styles.optionsContainer}>
          <div style={styles.optionRow}>
            {/* Option A */}
            <div style={styles.option}>
              <span style={styles.optionLabel}>(A)</span>
              <span style={styles.optionText}>
                {language === "english" 
                  ? (extractOptions(question.eng_option)[0] || question.option_a)
                  : (extractOptions(question.hindi_option)[0] || question.option_a_hindi)
                }
              </span>
            </div>
          </div>

          <div style={styles.optionRow}>
            {/* Option B */}
            <div style={styles.option}>
              <span style={styles.optionLabel}>(B)</span>
              <span style={styles.optionText}>
                {language === "english" 
                  ? (extractOptions(question.eng_option)[1] || question.option_b)
                  : (extractOptions(question.hindi_option)[1] || question.option_b_hindi)
                }
              </span>
            </div>
          </div>

          <div style={styles.optionRow}>
            {/* Option C */}
            <div style={styles.option}>
              <span style={styles.optionLabel}>(C)</span>
              <span style={styles.optionText}>
                {language === "english" 
                  ? (extractOptions(question.eng_option)[2] || question.option_c)
                  : (extractOptions(question.hindi_option)[2] || question.option_c_hindi)
                }
              </span>
            </div>
          </div>

          <div style={styles.optionRow}>
            {/* Option D */}
            <div style={styles.option}>
              <span style={styles.optionLabel}>(D)</span>
              <span style={styles.optionText}>
                {language === "english" 
                  ? (extractOptions(question.eng_option)[3] || question.option_d)
                  : (extractOptions(question.hindi_option)[3] || question.option_d_hindi)
                }
              </span>
            </div>
          </div>
        </div>
        
        {/* Reference Citation */}
       
      </div>
    );
  };
  
  const renderItem = isSolutionModule ? renderSolution : renderQuestion;

  const getTopicNameForSection = (questions) => {
    if (questions && questions.length > 0) {
      return questions[0].topic_name || "";
    }
    return "";
  };

  // Function to get the answer value from different possible field names
  const getAnswerValue = (question) => {
    // Check multiple possible field names for the answer
    const possibleFields = ['answer', 'correct_option', 'correct_answer', 'correct_ans', 'correctAnswer'];
    
    for (const field of possibleFields) {
      if (question[field] !== undefined && question[field] !== null) {
        return question[field];
      }
    }
    
    // If no answer field is found, check if there's a "correct" field that might contain the answer index (0,1,2,3)
    if (question.correct !== undefined) {
      // Convert numeric index to letter (0->A, 1->B, etc.)
      const answerLetters = ['A', 'B', 'C', 'D'];
      return answerLetters[question.correct] || question.correct;
    }
    
    return ""; // Return empty string if no answer field is found
  };

  // Function to render answer key
  const renderAnswerKey = () => {
    // Create a flat array of all questions with section info
    const allQuestions = [];
    let questionCounter = 0;
    
    // Iterate through nested structure to extract all questions
    Object.entries(groupedQuestions).forEach(([exerciseLevel, questionTypes]) => {
      Object.entries(questionTypes).forEach(([type, subTopics]) => {
        Object.entries(subTopics).forEach(([subTopic, subSubTopics]) => {
          Object.entries(subSubTopics).forEach(([subSubTopic, questions]) => {
            questions.forEach((question, idx) => {
              questionCounter++;
              allQuestions.push({
                question,
                number: questionCounter,
                sectionInfo: {
                  exerciseLevel,
                  type,
                  subTopic: subTopic !== "General" ? subTopic : "",
                  subSubTopic: subSubTopic !== "General" ? subSubTopic : "",
                }
              });
            });
          });
        });
      });
    });
    
    // Calculate how many rows we need (8 answers per row)
    const rows = Math.ceil(allQuestions.length / 8);
    const answerRows = [];
    
    for (let i = 0; i < rows; i++) {
      const startIdx = i * 8;
      const rowQuestions = allQuestions.slice(startIdx, startIdx + 8);
      
      answerRows.push(
        <div key={`row-${i}`} style={styles.answerRow}>
          {rowQuestions.map((item) => {
            const answerValue = getAnswerValue(item.question);
            
            return (
              <div key={`answer-${item.number}`} style={styles.answerItem}>
                {item.number}. <span style={styles.answerValue}>{answerValue}</span>
              </div>
            );
          })}
        </div>
      );
    }
    
    if (allQuestions.length === 0) {
      return null;
    }
    
    return (
      <div style={styles.masterAnswerKeyContainer}>
        <h2 style={styles.masterAnswerKeyTitle}>Answer Keys</h2>
        <div style={styles.answerKeyContainer}>
          {answerRows}
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        @media print {
          @page {
            size: 12in 15in;
            margin: .8in .5in .8in .5in;
          }
          
          /* Print reset - ensure everything is visible */
          body {
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          
          /* Ensure all content is visible and printable */
          .sample-question-container,
          .sample-question-container * {
            visibility: visible !important;
            color: black !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          .sample-question-container {
            width: 100% !important;
            max-width: none !important;
            margin: 0 auto !important;
            padding: 20px !important;
            display: block !important;
            position: static !important;
            left: auto !important;
            top: 0 !important;
            right: auto !important;
            bottom: auto !important;
          
            height: 100% !important;
            min-height: auto !important;
            overflow: visible !important;
          }
          
          .sample-question-two-column {
            display: flex !important;
            gap: 40px !important;
            visibility: visible !important;
            width: 100% !important;
          }
          
          .sample-question-column {
            width: 46% !important;
            box-sizing: border-box;
            padding-right: 25px;
            visibility: visible !important;
            display: block !important;
            flex: 0 0 46% !important;
          }
          
          .sample-question-item {
            page-break-inside: avoid;
            margin-bottom: 15px;
            visibility: visible !important;
            display: block !important;
            width: 100% !important;
            overflow: visible !important;
            break-inside: avoid;
          }
          
          /* Ensure exercise sections are visible */
          .sample-question-container > div {
            display: block !important;
            visibility: visible !important;
            page-break-inside: avoid;
            margin-bottom: 30px;
          }
          
          /* Ensure all text content is visible */
          .sample-question-container div,
          .sample-question-container span,
          .sample-question-container p,
          .sample-question-container h1,
          .sample-question-container h2,
          .sample-question-container h3,
          .sample-question-container h4,
          .sample-question-container h5,
          .sample-question-container h6 {
            visibility: visible !important;
            color: black !important;
          }
          
                     /* Images should be visible */
           .sample-question-container img {
             visibility: visible !important;
             display: inline-block !important;
             max-width: 100% !important;
             height: auto !important;
           }
           
           /* Header elements for print */
           .sample-question-header,
           .sample-question-logo,
           .sample-question-title,
           .sample-question-subject,
           .sample-question-hr {
             visibility: visible !important;
             display: block !important;
           }
           
           /* Keep header and first exercise section together */
           .sample-question-header {
             page-break-after: avoid !important;
             break-after: avoid !important;
           }
           
           .sample-question-hr {
             visibility: visible !important;
             display: block !important;
             border: none !important;
             height: 2px !important;
             background-color: #333 !important;
             page-break-after: avoid !important;
             break-after: avoid !important;
             margin-bottom: 0 !important;
           }
           
           /* Prevent page break before first exercise section */
           .sample-first-exercise,
           .sample-exercise-section:first-of-type,
           .sample-question-hr + .sample-exercise-section {
             page-break-before: avoid !important;
             break-before: avoid !important;
           }
           
           /* Wrap header and first section together */
           .sample-question-header + hr + .sample-exercise-section {
             page-break-before: avoid !important;
             break-before: avoid !important;
           }
           
           .sample-question-logo img {
             visibility: visible !important;
             display: inline-block !important;
             max-width: 300px !important;
             height: auto !important;
           }
          .redline {
            visibility: visible !important;
            display: block !important;
            height: 2px !important;
            background-color: black !important;
            margin: 2px 0 !important;
            width: 100% !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
           
           .sample-question-title,
           .sample-question-subject {
             visibility: visible !important;
             display: block !important;
             color: #b32424 !important;
           }
         }
       `}</style>
       <div style={{...styles.container, ...styles.printable}} className="sample-question-container">
         {/* Header Section with Logo and Subject */}
         <div style={styles.printHead} className="sample-question-header">
           <div style={styles.logoSection} className="sample-question-logo">
             <img src={logo} style={styles.logo} alt="Motion" />
           </div>
           <h5 style={styles.title} className="sample-question-title">
             <b>{isSolutionModule ? "Sample Solution" : "Sample Question"}</b>
           </h5>
           <h4 style={styles.subName} className="sample-question-subject">
             <b>{responseData.length > 0 ? responseData[0].sub_name : ""}</b>
           </h4>
         </div>
         <hr style={styles.hrLine} className="sample-question-hr" />
         
         {Object.entries(groupedQuestions).map(([exerciseLevel, questionTypes], index) => (
        <div key={exerciseLevel} style={styles.exerciseSection} className={index === 0 ? "sample-exercise-section sample-first-exercise" : "sample-exercise-section"}>
          {/* Dots and Line Header */}
          <div style={styles.exerciseHeader}>
            <div style={styles.exerciseDots}>•••</div>
            <div style={styles.redLine} className="redline" />
          </div>
          
          {/* Exercise Title Row */}
          <div style={styles.exerciseTitleRow}>
            <div style={styles.exerciseTitle}>{exerciseLevel}</div>
            <div style={styles.questionType}>
              {Object.keys(questionTypes)[0] || "Single Correct"}
            </div>
          </div>
          
          <div style={styles.redLine} className="redline" />
          
          {/* Questions by type */}
          {Object.entries(questionTypes).map(([type, subTopics]) => {
            // Get all questions from this question type to extract the common topic name
            const allQuestionsInType = [];
            Object.values(subTopics).forEach(subSubTopics => {
              Object.values(subSubTopics).forEach(questions => {
                allQuestionsInType.push(...questions);
              });
            });
            
            const topicName = getTopicNameForSection(allQuestionsInType);
            
            return (
              <div key={type}>
                {/* Main Subject/Topic Name - Center aligned */}
                {topicName && (
                  <div style={styles.subjectName}>
                    {topicName}
                  </div>
                )}
                
                {/* Render by sub_topics */}
                {Object.entries(subTopics).map(([subTopic, subSubTopics]) => (
                  <div key={subTopic} style={styles.subTopicSection}>
                    {/* Sub Topic Heading */}
                    <div style={styles.subTopicHeading}>
                      {subTopic !== "General" ? subTopic : ""}
                    </div>
                    
                    {/* Render by sub_sub_topics */}
                    {Object.entries(subSubTopics).map(([subSubTopic, questions]) => (
                      <div key={subSubTopic} style={styles.subSubTopicSection}>
                        {/* Sub-Sub Topic Heading (if not General) */}
                        {subSubTopic !== "General" && (
                          <div style={styles.subSubTopicHeading}>
                            {subSubTopic}
                          </div>
                        )}
                        
                        {/* Questions in two columns */}
                        <div style={styles.twoColumnLayout} className="sample-question-two-column">
                          {/* Left Column */}
                          <div style={styles.column} className="sample-question-column">
                            {questions.filter((_, i) => i % 2 === 0).map((question, index) => {
                              return renderItem(question, 2 * index, questions.length);
                            })}
                          </div>
                          
                          {/* Right Column */}
                          <div style={styles.column} className="sample-question-column">
                            {questions.filter((_, i) => i % 2 === 1).map((question, index) => {
                              return renderItem(question, 2 * index + 1, questions.length);
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ))}

      {/* Answer Key Section at the bottom */}
      {!isSolutionModule && renderAnswerKey()}
      </div>
    </>
  );
};


// Inline styles based on the provided CSS with additional styles for sub-topics
const styles = {
  container: {
    padding: '20px',
    // maxWidth: '1200px',
    margin: '0 auto',
    color: 'black',
    fontFamily: 'Times New Roman, Times, serif',
  },
  exerciseSection: {
    marginBottom: '30px',
    pageBreakInside: 'avoid',
  },
  exerciseHeader: {
    display: 'flex',
    flexDirection: 'column',
  },
  exerciseDots: {
    textAlign: 'center',
    fontSize: '14px',
    letterSpacing: '2px',
    color: 'black',
  },
  redLine: {
    height: '2px',
    backgroundColor: 'black',
    margin: '2px 0',
  },
  exerciseTitleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '8px 0',
  },
  exerciseTitle: {
    color: 'black',
    fontWeight: 'bold',
    fontSize: '25px',
  },
  questionType: {
    color: 'black',
    fontWeight: 'bold',
    fontSize: '25px',
    textAlign: 'right',
  },
  subjectName: {
    fontWeight: 'bold',
    margin: '12px 0',
    fontSize: '14px',
    color: 'black',
    textAlign: 'center',
  },
  subTopicSection: {
    marginTop: '20px',
    marginBottom: '25px',
  },
  subTopicHeading: {
    fontWeight: 'bold',
    fontSize: '18px',
    color: 'black',
    borderBottom: '1px solid #ccc',
    paddingBottom: '5px',
    marginBottom: '15px',
  },
  subSubTopicSection: {
    marginBottom: '20px',
  },
  subSubTopicHeading: {
    fontWeight: 'bold',
    fontSize: '16px',
    color: 'black',
    fontStyle: 'italic',
    marginLeft: '15px',
    marginBottom: '10px',
  },
  twoColumnLayout: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: '15px',
    gap: '40px',
  },
  column: {
    width: '48%',
    boxSizing: 'border-box',
    paddingRight: '35px',
  },
  questionItem: {
    marginBottom: '15px',
    pageBreakInside: 'avoid',
  },
  questionRow: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '10px',
  },
  questionNumber: {
    color: 'black',
    fontWeight: '500',
    minWidth: '20px',
    marginRight: '5px',
    fontSize: '21px',
  },
  questionText: {
    flex: '1',
    fontSize: '21px',
    fontWeight: '500',
    color: 'black',
    fontFamily: 'Times New Roman, Times, serif',
  },
  qrCode: {
    marginLeft: '10px',
    width: '60px',
    height: '60px',
  },
  optionsContainer: {
    marginLeft: '25px',
    marginTop: '5px',
  },
  optionRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: '5px',
  },
  option: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    fontSize: '21px',
    marginBottom: '5px',
  },
  optionLabel: {
    minWidth: '25px',
    fontSize: '21px',
    color: 'black',
    marginRight: '5px',
  },
  optionText: {
    flex: '1',
    fontSize: '21px',
    fontWeight: '500',
    fontFamily: 'Times New Roman, Times, serif',
    color: 'black',
  },
  solutionHeading: {
    fontSize: '21px',
    fontWeight: 'bold',
    color: 'black',
    fontFamily: 'Times New Roman, Times, serif',
  },
  solutionText: {
    marginLeft: '25px',
    marginTop: '5px',
    fontSize: '21px',
    fontWeight: '500',
    fontFamily: 'Times New Roman, Times, serif',
    color: 'black',
    lineHeight: 1.5,
  },
  reference: {
    marginLeft: '25px',
    fontSize: '13px',
    color: 'black',
    textAlign: 'right',
    marginTop: '5px',
    fontStyle: 'italic',
    fontWeight: 'bold',
  },
  
  // Answer Key Styles
  masterAnswerKeyContainer: {
    marginTop: '60px',
    padding: '30px',
    backgroundColor: '#f9f9f9',
    border: '1px solid #ddd',
    borderRadius: '8px',
    pageBreakBefore: 'always',
  },
  masterAnswerKeyTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '30px',
    color: '#333',
    position: 'relative',
    textTransform: 'uppercase',
    paddingBottom: '10px',
    borderBottom: '2px solid #000',
  },
  answerKeyContainer: {
    width: '100%',
  },
  logo: {
    width: '25%',
  },
  answerRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    marginBottom: '15px',
  },
  answerItem: {
    width: '12.5%', // 8 items per row
    padding: '8px 12px',
    fontSize: '18px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    color: 'black',
    fontFamily: 'Times New Roman, Times, serif',
  },
  answerValue: {
    marginLeft: '5px',
    fontWeight: 'bold',
    color: '#c00000', // Dark red color
  },
  printable: {
    visibility: 'visible',
    boxShadow: 'none',
    position: 'relative',
  },
  printHead: {
    display: 'block',
    width: '100%',
  },
  logoSection: {
    textAlign: 'center',
    marginBottom: '20px',
    // width:'25%',
  },
  title: {
    fontSize: '1.3rem',
    textAlign: 'center',
    margin: '0.5rem auto',
    // color: '#b32424',
    fontWeight: 'bold',
  },
  subName: {
    fontSize: '2.5rem',
    margin: '0.5rem auto',
    textAlign: 'center',
    // color: '#b32424',
    marginTop: '2rem',
    fontWeight: 'bold',
  },
  hrLine: {
    border: 'none',
    height: '2px',
    backgroundColor: '#333',
    opacity: 1,
    margin: '1rem 0',
  }
  
  
};

export default SampleQuestionModuleRender;